<template>
  <v-container>
    <v-card max-width="600" class="mx-auto">
      <v-toolbar dark color="primary">
        <v-toolbar-title>{{ $t('notasFiscais.activateSetCode.title') }}</v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <v-container>
          <v-alert v-if="loadError" type="error" dense class="mb-4">
            {{ loadError }}
          </v-alert>

          <!-- Step 1: Select Nota Fiscal -->
          <v-autocomplete
            v-model="selectedNotaId"
            :items="notasFiscaisList"
            item-text="displayLabel"
            item-value="id_nota_fiscal"
            :label="$t('notasFiscais.activateSetCode.selectNotaFiscal')"
            clearable
            :loading="loadingNotas"
            class="mb-4"
            @change="onNotaSelected"
          ></v-autocomplete>

          <!-- Step 2: Select Item (when NF selected) -->
          <v-autocomplete
            v-if="selectedNota"
            v-model="selectedItemId"
            :items="(selectedNota.itens || []).map(i => ({ ...i, displayLabel: `${i.n_item} — ${(i.x_prod || '').toString().trim() || i.c_prod || i.id_item}` }))"
            item-text="displayLabel"
            item-value="id_item"
            :label="$t('notasFiscais.activateSetCode.selectItem')"
            clearable
            class="mb-4"
            @change="onItemSelected"
          ></v-autocomplete>

          <!-- Step 3: Activation form (when item selected) -->
          <template v-if="itemContext">
            <v-alert type="info" dense class="mb-4">
              {{ $t('notasFiscais.items') }}: {{ selectedNota.nome_nota || selectedNota.chave_nfe || '' }} — {{ itemContext.x_prod || itemContext.n_item }}
            </v-alert>
            <v-alert v-if="!loadingEquipamentosForActivate && equipamentosForActivate.length === 0" type="info" dense class="mb-4">
              {{ $t('notasFiscais.activateSetCode.noEquipmentsLinked') }}
            </v-alert>
            <v-autocomplete
              v-model="activateSetCodeForm.id_equipamento"
              :items="equipamentosForActivate.map(e => ({ ...e, displayLabel: e.numero_serie && String(e.numero_serie).trim() !== '' ? `${e.nome_equipamento} (${e.numero_serie})` : e.nome_equipamento }))"
              item-text="displayLabel"
              item-value="id_equipamento"
              :label="labelText('notasFiscais.activateSetCode.selectEquipment')"
              :rules="[v => !!v || $t('equipment.nameRequired')]"
              clearable
              :loading="loadingEquipamentosForActivate"
              @change="onActivateEquipmentSelected"
            >
              <template v-slot:selection="{ item }">
                {{ item.nome_equipamento }} ({{ item.codigo_qrcode || '—' }})
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.numero_serie ? item.numero_serie + ' — ' : null }}{{ item.nome_equipamento }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.codigo_qrcode || '—' }} · {{ item.ativo_extenso }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <v-switch
              v-model="activateSetCodeForm.ativo"
              :label="$t('notasFiscais.activateSetCode.active')"
              class="mt-2"
            ></v-switch>
            <v-autocomplete
              v-model="activateSetCodeForm.codigo_qrcode"
              :items="equipmentCodeOptionsForActivate"
              item-text="codigo"
              item-value="codigo"
              :label="labelText('notasFiscais.activateSetCode.code')"
              clearable
              :filter="(item, queryText) => (item.codigo || '').toLowerCase().includes((queryText || '').toLowerCase())"
            >
              <template v-slot:append-outer>
                <v-btn icon small @click="openQRScanner" color="primary">
                  <v-icon>mdi-qrcode-scan</v-icon>
                </v-btn>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.codigo }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </template>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="resetForm">{{ $t('button.cancel') }}</v-btn>
        <v-btn
          color="primary"
          :loading="savingActivateSetCode"
          :disabled="!itemContext || !activateSetCodeForm.id_equipamento"
          @click="saveActivateSetCode"
        >
          {{ $t('button.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Equipment QR Scanner Dialog -->
    <v-dialog v-model="qrScannerDialog" max-width="800" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ $t('equipment.qrScanner.title') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeQRScanner">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-container>
            <v-alert v-if="qrScannerError" type="error" dense class="mb-4">
              {{ qrScannerError }}
            </v-alert>
            <v-alert type="info" dense class="mb-4">
              {{ $t('equipment.qrScanner.instructions') }}
            </v-alert>
            <div style="position: relative; width: 100%; max-width: 600px; margin: 0 auto;">
              <qrcode-stream
                v-if="qrScannerDialog"
                @decode="onQRDecode"
                @init="onQRInit"
                :camera="qrCamera"
                style="width: 100%; border-radius: 8px;"
              ></qrcode-stream>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeQRScanner">
            {{ $t('button.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import { QrcodeStream } from 'vue-qrcode-reader';

export default {
  name: 'ActivateEquipamentoNotaFiscal',
  components: { QrcodeStream },
  data() {
    return {
      token: null,
      loadingNotas: false,
      notasFiscaisList: [],
      selectedNotaId: null,
      selectedNota: null,
      selectedItemId: null,
      itemContext: null,
      loadError: null,
      tiposequipamentos: [],
      activateSetCodeForm: {
        id_equipamento: null,
        ativo: true,
        codigo_qrcode: null,
        cod_equipamento: null,
        nome_equipamento: null,
        cod_integracao: null,
        cod_integracao_nivel_acesso: null,
        numero_serie: null,
        id_tipo_equipamento: null,
        id_veiculo: null,
        modelo: null,
        id_status_equipamento: null
      },
      equipamentosForActivate: [],
      loadingEquipamentosForActivate: false,
      availableQRCodes: [],
      savingActivateSetCode: false,
      qrScannerDialog: false,
      qrScannerError: null,
      qrCamera: 'auto'
    };
  },
  computed: {
    equipmentCodeOptionsForActivate() {
      const current = this.activateSetCodeForm.codigo_qrcode;
      if (!current || (typeof current === 'string' && !current.trim())) return this.availableQRCodes || [];
      if ((this.availableQRCodes || []).some((item) => item.codigo === current)) return this.availableQRCodes || [];
      return [{ codigo: current }].concat(this.availableQRCodes || []);
    }
  },
  created() {
    this.token = sessionStorage.getItem('api');
    this.loadError = null;
    this.loadNotasFiscais();
    this.loadTiposEquipamentos();
  },
  methods: {
    labelText(key, tcCount) {
      const v = tcCount !== undefined ? this.$tc(key, tcCount) : this.$t(key);
      if (typeof v === 'string') return v;
      if (v && typeof v === 'object' && typeof v.label === 'string') return v.label;
      return key;
    },
    getTipoEquipamentoIdFromLineDescription(descricao) {
      if (!descricao || typeof descricao !== 'string') return null;
      const d = descricao.trim().toLowerCase();
      if (!d) return null;
      const tipos = this.tiposequipamentos || [];
      const exact = tipos.find((t) => (t.nome_tipo_equipamento || '').toLowerCase() === d);
      if (exact) return exact.id_tipo_equipamento;
      const partial = tipos.find((t) => {
        const nome = (t.nome_tipo_equipamento || '').toLowerCase();
        return nome && (d.includes(nome) || nome.includes(d));
      });
      return partial ? partial.id_tipo_equipamento : null;
    },
    loadNotasFiscais() {
      this.loadingNotas = true;
      apiController.request(apiController.endpoints.getallNotasfiscais, {
        onSuccess: (response) => {
          const data = response.data;
          const arr = Array.isArray(data)
            ? data
            : (data && Array.isArray(data.data) ? data.data : (data && Array.isArray(data.rows) ? data.rows : []));
          this.notasFiscaisList = (arr || []).map((nf) => ({
            ...nf,
            displayLabel: (nf.nome_nota || nf.chave_nfe || `ID ${nf.id_nota_fiscal}`).toString().trim()
          }));
          this.loadingNotas = false;
        },
        onError: (e) => {
          this.loadingNotas = false;
          this.loadError = (e.response && e.response.data && e.response.data.mensagem) || e.message || 'Failed to load notas fiscais.';
        }
      });
    },
    loadTiposEquipamentos() {
      return apiController.request(apiController.endpoints.getallTiposequipamentos, {
        onSuccess: (response) => { this.tiposequipamentos = response.data || []; }
      });
    },
    onNotaSelected(idNotaFiscal) {
      this.selectedItemId = null;
      this.itemContext = null;
      this.equipamentosForActivate = [];
      this.resetActivateForm();
      if (!idNotaFiscal) {
        this.selectedNota = null;
        return;
      }
      this.loadError = null;
      apiController.request(apiController.endpoints.getNotafiscalbyid, {
        params: { id: idNotaFiscal },
        onSuccess: (response) => {
          this.selectedNota = response.data;
        },
        onError: (e) => {
          this.selectedNota = null;
          this.loadError = (e.response && e.response.data && e.response.data.mensagem) || e.message || 'Failed to load nota fiscal.';
        }
      });
    },
    onItemSelected(idItem) {
      this.equipamentosForActivate = [];
      this.resetActivateForm();
      if (!idItem || !this.selectedNota || !this.selectedNota.itens) {
        this.itemContext = null;
        return;
      }
      const idItemNum = Number(idItem);
      this.itemContext = this.selectedNota.itens.find((i) => i.id_item === idItemNum || Number(i.id_item) === idItemNum) || null;
      if (this.itemContext) {
        this.loadEquipamentosForActivate();
        this.loadAvailableQRCodes();
      }
    },
    resetActivateForm() {
      this.activateSetCodeForm = {
        id_equipamento: null,
        ativo: true,
        codigo_qrcode: null,
        cod_equipamento: null,
        nome_equipamento: null,
        cod_integracao: null,
        cod_integracao_nivel_acesso: null,
        numero_serie: null,
        id_tipo_equipamento: null,
        id_veiculo: null,
        modelo: null,
        id_status_equipamento: null
      };
    },
    resetForm() {
      this.loadError = null;
      this.selectedNotaId = null;
      this.selectedNota = null;
      this.selectedItemId = null;
      this.itemContext = null;
      this.equipamentosForActivate = [];
      this.resetActivateForm();
    },
    loadEquipamentosForActivate() {
      const idNotaFiscal = this.selectedNota && this.selectedNota.id_nota_fiscal;
      if (!idNotaFiscal || !this.itemContext) return;
      this.loadingEquipamentosForActivate = true;
      const item = this.itemContext;
      const idTipoLine = item.id_tipo_equipamento || this.getTipoEquipamentoIdFromLineDescription(item.x_prod);
      apiController.request(apiController.endpoints.getEquipamentosbynotafiscal, {
        params: { id: idNotaFiscal },
        onSuccess: (r) => {
          let list = r.data && Array.isArray(r.data) ? r.data : [];
          if (idTipoLine != null) {
            list = list.filter((e) => e.id_tipo_equipamento === idTipoLine);
          }
          this.equipamentosForActivate = list;
          this.loadingEquipamentosForActivate = false;
        },
        onError: () => {
          this.loadingEquipamentosForActivate = false;
        }
      });
    },
    loadAvailableQRCodes() {
      return apiController.request(apiController.endpoints.getAvailableqrcodesequipamentos)
        .then((response) => {
          this.availableQRCodes = response.data || [];
          if (this.availableQRCodes.length === 0) {
            return apiController.request(apiController.endpoints.ensureqrcodepool)
              .then(() => this.loadAvailableQRCodes());
          }
        })
        .catch(() => {
          this.availableQRCodes = [];
        });
    },
    onActivateEquipmentSelected(id) {
      const eq = (this.equipamentosForActivate || []).find((e) => e.id_equipamento === id);
      if (eq) {
        this.activateSetCodeForm.ativo = true;
        this.activateSetCodeForm.codigo_qrcode = eq.codigo_qrcode;
        this.activateSetCodeForm.cod_equipamento = eq.cod_equipamento;
        this.activateSetCodeForm.nome_equipamento = eq.nome_equipamento;
        this.activateSetCodeForm.cod_integracao = eq.cod_integracao;
        this.activateSetCodeForm.cod_integracao_nivel_acesso = eq.cod_integracao_nivel_acesso;
        this.activateSetCodeForm.numero_serie = eq.numero_serie;
        this.activateSetCodeForm.id_tipo_equipamento = eq.id_tipo_equipamento;
        this.activateSetCodeForm.id_veiculo = eq.id_veiculo;
        this.activateSetCodeForm.modelo = eq.modelo;
        this.activateSetCodeForm.id_status_equipamento = eq.id_status_equipamento;
      }
    },
    openQRScanner() {
      this.qrScannerError = null;
      this.qrScannerDialog = true;
    },
    closeQRScanner() {
      this.qrScannerDialog = false;
      this.qrScannerError = null;
      this.qrCamera = 'auto';
    },
    onQRDecode(decodedString) {
      try {
        this.activateSetCodeForm.codigo_qrcode = decodedString;
        this.closeQRScanner();
        EventBus.$emit('show-alert', {
          mensagem: this.$t('equipment.qrScanner.success'),
          tipo: 'success'
        });
      } catch (error) {
        this.qrScannerError = this.$t('equipment.qrScanner.invalidJSON');
      }
    },
    onQRInit(promise) {
      promise
        .then(() => { this.qrScannerError = null; })
        .catch((error) => {
          let errorMessage = this.$t('equipment.qrScanner.cameraError');
          if (error.name === 'NotAllowedError') {
            errorMessage = this.$t('equipment.qrScanner.cameraPermissionDenied');
          } else if (error.name === 'NotFoundError') {
            errorMessage = this.$t('equipment.qrScanner.cameraNotFound');
          } else if (error.name === 'NotReadableError') {
            errorMessage = this.$t('equipment.qrScanner.cameraNotReadable');
          } else if (error.name === 'OverconstrainedError') {
            errorMessage = this.$t('equipment.qrScanner.cameraConstraints');
          }
          this.qrScannerError = errorMessage;
          EventBus.$emit('show-alert', { mensagem: errorMessage, tipo: 'error' });
        });
    },
    saveActivateSetCode() {
      if (!this.activateSetCodeForm.id_equipamento) {
        EventBus.$emit('show-error', { error: { message: this.$t('notasFiscais.activateSetCode.error') } });
        return;
      }
      this.savingActivateSetCode = true;

      const resolveCodeFromPool = () => {
        const selected = this.activateSetCodeForm.codigo_qrcode;
        if (selected && (typeof selected === 'string' ? selected.trim() : selected)) {
          return Promise.resolve(selected);
        }
        const pool = this.availableQRCodes || [];
        if (pool.length > 0 && pool[0].codigo) {
          return Promise.resolve(pool[0].codigo);
        }
        return apiController.request(apiController.endpoints.ensureqrcodepool)
          .then(() => apiController.request(apiController.endpoints.getAvailableqrcodesequipamentos))
          .then((r) => {
            const codes = r.data || [];
            if (codes.length > 0 && codes[0].codigo) return codes[0].codigo;
            return null;
          });
      };

      resolveCodeFromPool()
        .then((codigoQrcodeValue) => {
          const code = codigoQrcodeValue || null;
          const payload = {
            id_equipamento: this.activateSetCodeForm.id_equipamento,
            nome_equipamento: this.activateSetCodeForm.nome_equipamento,
            cod_equipamento: this.activateSetCodeForm.cod_equipamento || null,
            codigo_qrcode: code,
            cod_integracao: this.activateSetCodeForm.cod_integracao || null,
            cod_integracao_nivel_acesso: this.activateSetCodeForm.cod_integracao_nivel_acesso || null,
            numero_serie: this.activateSetCodeForm.numero_serie || null,
            id_tipo_equipamento: this.activateSetCodeForm.id_tipo_equipamento || null,
            ativo: this.activateSetCodeForm.ativo ? 1 : 0,
            id_veiculo: this.activateSetCodeForm.id_veiculo || null,
            modelo: this.activateSetCodeForm.modelo || null,
            id_status_equipamento: this.activateSetCodeForm.id_status_equipamento || 0,
            token: this.token
          };
          return apiController.request(apiController.endpoints.updateEquipamento, { data: payload });
        })
        .then((response) => {
          this.savingActivateSetCode = false;
          if (response.data && response.data.erro) {
            const msg = response.data.mensagem || this.$t('notasFiscais.activateSetCode.error');
            EventBus.$emit('show-error', { error: { message: msg } });
            return;
          }
          EventBus.$emit('show-success', { message: this.$t('notasFiscais.activateSetCode.success') });
          EventBus.$emit('show-alert', { mensagem: this.$t('notasFiscais.activateSetCode.success'), tipo: 'success' });
          this.loadAvailableQRCodes();
          this.loadEquipamentosForActivate();
          this.resetActivateForm();
        })
        .catch(() => {
          this.savingActivateSetCode = false;
        });
    }
  }
};
</script>
